import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, LayoutGrid, List, Search, ArrowRightLeft, CalendarClock } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/StateBlocks";
import { KanbanBoard } from "@/components/pipeline/KanbanBoard";
import { LeadCard } from "@/components/pipeline/LeadCard";
import { ChangeStageDialog } from "@/components/pipeline/ChangeStageDialog";
import { crmService, emitCrmChanged } from "@/services/crmService";
import { useCrmActions } from "@/context/CrmActionsContext";
import { LEAD_TYPES, PRIORITIES, TEMPERATURES, leadTypeLabel, priorityLabel, temperatureLabel } from "@/config/crmOptions";
import { fmtDate, priceRange } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const ALL = "__all__";

export default function Pipeline() {
  const { openNewLead } = useCrmActions();
  const navigate = useNavigate();
  const [view, setView] = useState("board");
  const [columns, setColumns] = useState([]);
  const [stages, setStages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [changeLead, setChangeLead] = useState(null);
  // list filters
  const [search, setSearch] = useState("");
  const [fType, setFType] = useState(ALL);
  const [fPriority, setFPriority] = useState(ALL);
  const [fStage, setFStage] = useState(ALL);
  const [sort, setSort] = useState("recent");

  const loadBoard = useCallback(async () => {
    const data = await crmService.board();
    setColumns(data.columns);
  }, []);

  const loadList = useCallback(async () => {
    const { leads } = await crmService.listLeads({
      search: search || undefined,
      lead_type: fType === ALL ? undefined : fType,
      priority: fPriority === ALL ? undefined : fPriority,
      stage: fStage === ALL ? undefined : fStage,
      sort,
    });
    setLeads(leads);
  }, [search, fType, fPriority, fStage, sort]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const s = await crmService.listStages();
      setStages(s);
      await Promise.all([loadBoard(), loadList()]);
    } catch (e) {
      toast.error("Could not load pipeline");
    } finally {
      setLoading(false);
    }
  }, [loadBoard, loadList]);

  useEffect(() => { refresh(); }, []); // eslint-disable-line
  useEffect(() => { if (!loading) loadList(); }, [loadList]); // eslint-disable-line
  useEffect(() => {
    const h = () => refresh();
    window.addEventListener("crm:changed", h);
    return () => window.removeEventListener("crm:changed", h);
  }, [refresh]);

  const totalLeads = columns.reduce((n, c) => n + c.leads.length, 0);

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination || source.droppableId === destination.droppableId) return;
    // Optimistic move.
    const prev = columns;
    const next = columns.map((c) => ({ ...c, leads: [...c.leads] }));
    const from = next.find((c) => c.stage.id === source.droppableId);
    const to = next.find((c) => c.stage.id === destination.droppableId);
    const idx = from.leads.findIndex((l) => l.id === draggableId);
    if (idx === -1) return;
    const [moved] = from.leads.splice(idx, 1);
    to.leads.splice(destination.index, 0, moved);
    setColumns(next);
    try {
      await crmService.changeStage(draggableId, destination.droppableId);
      toast.success(`Moved to ${to.stage.name}`);
      emitCrmChanged();
    } catch (e) {
      setColumns(prev);
      toast.error(e.message || "Could not move lead — reverted");
    }
  };

  const changeStage = async (leadId, toStageId) => {
    try {
      await crmService.changeStage(leadId, toStageId);
      toast.success("Stage updated");
      emitCrmChanged();
    } catch (e) {
      toast.error(e.message || "Could not change stage");
    }
  };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="pipeline-page">
        <PageHeader
          eyebrow="AgentHQ · Sell"
          title="Pipeline"
          description="Track every opportunity from new lead to closed — drag to move stages."
          actions={
            <div className="flex items-center gap-2">
              <div className="hidden items-center rounded-lg border border-border p-1 md:flex" data-testid="pipeline-view-toggle">
                <button onClick={() => setView("board")} data-testid="pipeline-view-board" className={`flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm ${view === "board" ? "bg-brand-navy text-white" : "text-muted-foreground"}`}><LayoutGrid className="h-4 w-4" /> Board</button>
                <button onClick={() => setView("list")} data-testid="pipeline-view-list" className={`flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm ${view === "list" ? "bg-brand-navy text-white" : "text-muted-foreground"}`}><List className="h-4 w-4" /> List</button>
              </div>
              <Button onClick={() => openNewLead()} data-testid="pipeline-new-lead"><Flame className="h-4 w-4" /> New Lead</Button>
              <Button variant="outline" onClick={() => navigate("/follow-ups")} data-testid="pipeline-followups"><CalendarClock className="h-4 w-4" /> Follow-Ups</Button>
            </div>
          }
        />

        {loading ? (
          <div className="py-10"><LoadingState label="Loading pipeline" /></div>
        ) : totalLeads === 0 ? (
          <EmptyState
            title="Your pipeline is ready."
            description="Add your first lead to start tracking opportunities."
            action={<Button className="mt-5" onClick={() => openNewLead()} data-testid="pipeline-empty-new-lead"><Flame className="h-4 w-4" /> New Lead</Button>}
          />
        ) : (
          <>
            {/* Desktop: board or list */}
            <div className="hidden md:block">
              {view === "board" ? (
                <KanbanBoard columns={columns} onDragEnd={onDragEnd} />
              ) : (
                <LeadListView
                  leads={leads} stages={stages}
                  search={search} setSearch={setSearch}
                  fType={fType} setFType={setFType}
                  fPriority={fPriority} setFPriority={setFPriority}
                  fStage={fStage} setFStage={setFStage}
                  sort={sort} setSort={setSort}
                  onChangeStage={(l) => setChangeLead(l)}
                  navigate={navigate}
                />
              )}
            </div>

            {/* Mobile: stage-grouped list with Change Stage action */}
            <div className="space-y-5 md:hidden" data-testid="pipeline-mobile">
              {columns.filter((c) => c.leads.length > 0).map((col) => (
                <div key={col.stage.id}>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground">{col.stage.name}</span>
                    <Badge variant="secondary">{col.leads.length}</Badge>
                  </div>
                  <div className="space-y-2">
                    {col.leads.map((lead) => (
                      <LeadCard key={lead.id} lead={lead} footer={
                        <button onClick={(e) => { e.stopPropagation(); setChangeLead(lead); }} data-testid={`mobile-change-stage-${lead.id}`}
                          className="mt-2 flex w-full items-center justify-center gap-1 rounded-md border border-border py-1.5 text-xs font-medium text-foreground hover:bg-secondary">
                          <ArrowRightLeft className="h-3 w-3" /> Change Stage
                        </button>
                      } />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <ChangeStageDialog
        open={!!changeLead}
        onOpenChange={(v) => !v && setChangeLead(null)}
        lead={changeLead}
        stages={stages}
        onChange={changeStage}
      />
    </AppShell>
  );
}

const LeadListView = ({ leads, stages, search, setSearch, fType, setFType, fPriority, setFPriority, fStage, setFStage, sort, setSort, onChangeStage, navigate }) => (
  <div className="space-y-4">
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex min-w-[200px] flex-1 items-center gap-2 rounded-lg border border-border px-3">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search leads…" className="border-0 px-0 shadow-none focus-visible:ring-0" data-testid="pipeline-list-search" />
      </div>
      <Select value={fStage} onValueChange={setFStage}>
        <SelectTrigger className="w-[150px]" data-testid="pipeline-filter-stage"><SelectValue placeholder="Stage" /></SelectTrigger>
        <SelectContent><SelectItem value={ALL}>All stages</SelectItem>{stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={fType} onValueChange={setFType}>
        <SelectTrigger className="w-[130px]" data-testid="pipeline-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
        <SelectContent><SelectItem value={ALL}>All types</SelectItem>{LEAD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={fPriority} onValueChange={setFPriority}>
        <SelectTrigger className="w-[130px]" data-testid="pipeline-filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
        <SelectContent><SelectItem value={ALL}>All priority</SelectItem>{PRIORITIES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={sort} onValueChange={setSort}>
        <SelectTrigger className="w-[150px]" data-testid="pipeline-list-sort"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="recent">Recently updated</SelectItem>
          <SelectItem value="created">Newest</SelectItem>
          <SelectItem value="followup">Next follow-up</SelectItem>
        </SelectContent>
      </Select>
    </div>

    {leads.length === 0 ? (
      <EmptyState title="No leads match" description="Adjust filters to see more opportunities." />
    ) : (
      <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-card">
        <Table data-testid="pipeline-list-table">
          <TableHeader>
            <TableRow className="bg-secondary/50">
              <TableHead>Contact</TableHead><TableHead>Type</TableHead><TableHead>Stage</TableHead>
              <TableHead>Budget</TableHead><TableHead>Location</TableHead><TableHead>Priority</TableHead>
              <TableHead>Temp</TableHead><TableHead>Follow-up</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.map((l) => (
              <TableRow key={l.id} className="cursor-pointer" data-testid={`lead-row-${l.id}`} onClick={() => l.contact?.id && navigate(`/crm/${l.contact.id}`)}>
                <TableCell className="font-medium">{l.contact?.first_name} {l.contact?.last_name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{leadTypeLabel(l.lead_type)}</TableCell>
                <TableCell><Badge variant="outline">{l.stage?.name || "—"}</Badge></TableCell>
                <TableCell className="text-sm text-muted-foreground">{priceRange(l.price_min, l.price_max) || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{l.desired_location || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{priorityLabel(l.priority)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{temperatureLabel(l.temperature)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{fmtDate(l.next_followup_at)}</TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" onClick={() => onChangeStage(l)} data-testid={`lead-change-stage-${l.id}`}>
                    <ArrowRightLeft className="h-3.5 w-3.5" /> Move
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )}
  </div>
);
